import { createRootRouteWithContext, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { LoginModal } from '@/features/Auth/LoginModal';
import { useInitAuth } from '@/features/Auth/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/features/Layout/AppLayout';

interface RouterContext {
  queryClient: QueryClient;
}

// Routes that should NOT show the sidebar/nav layout
const publicPaths = ['/login', '/auth/setup', '/auth/forgot-password', '/auth/reset-password', '/auth/oauth-callback'];

const noLayoutPaths = ['/admin', '/trainer', '/org'];

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ location }) => {
    const { isAuthenticated } = useAuthStore.getState();
    const isPublic = publicPaths.some((p) => location.pathname.startsWith(p));
    if (!isAuthenticated && !isPublic) {
      throw redirect({ to: '/login' });
    }
  },
  component: RootLayout,
});

function RootLayout() {
  useInitAuth();
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useRouterState({ select: (s) => s.location });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const isPublicRoute = publicPaths.some((p) => location.pathname.startsWith(p));
  const isNoLayout = noLayoutPaths.some((p) => location.pathname.startsWith(p));
  const isProblemDetail = /^\/problems\/[^/]+/.test(location.pathname);
  // A quiz attempt gets the same exemption as a problem. It used to render
  // inside the topic lesson shell, which meant arriving from Assessments with
  // a LessonHeader for a topic you did not pick, a tab bar with no active tab,
  // and a curriculum rail inviting you to navigate away from a timed attempt
  // that auto-submits. QuizShell supplies its own chrome.
  const isQuizAttempt = /^\/courses\/[^/]+\/topics\/[^/]+\/quiz\/[^/]+/.test(location.pathname);
  const showLayout =
    isAuthenticated && !isPublicRoute && !isNoLayout && !isProblemDetail && !isQuizAttempt;

  return (
    <>
      {showLayout ? (
        <AppLayout>
          <Outlet />
        </AppLayout>
      ) : (
        <Outlet />
      )}
      <LoginModal />
      <Toaster
        position="bottom-right"
        gap={8}
        toastOptions={{
          style: {
            borderRadius: '10px',
            fontSize: '13.5px',
            padding: '12px 16px',
          },
        }}
      />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
}
